import {createAsyncThunk, createReducer} from '@reduxjs/toolkit';

import {ENDPOINTS} from 'constants/endpoints';
import {CartItemType} from 'desktop/pages/cart/types';
import {apiConfig, HTTP_REQUEST_HEADER, HTTP_REQUEST_HEADER_CONTENT_TYPE_VALUE} from 'libs/api/api';
import {CartTotalsType, MessageType} from 'types/cartType';

import {setDataFromHostStore} from '../../actions';
import {AccessoryWindowType} from '../../reducers/accessories/accessoriesType';
import {SpecialOfferItemType} from '../quoteInfo/quoteInfoType';

type QuoteParamsType = {
    version?: string;
    itemIds?: Array<string>;
};

export type GetQuoteType = {
    quoteItems: CartItemType[];
    messages: MessageType[];
    accessories: AccessoryWindowType[];
    totals: CartTotalsType;
    id: string;
    specialOffer: string;
    cartKey: string | null;
    specialOffers: Array<SpecialOfferItemType> | null;
    count: number;
};

/**
 * Get quote params for quote request, itemsId and version.
 * This is temp solution while use get quote v1.0, when backend get version 2.0
 * @typedef {Object} QuoteParams
 * @property {string} [version] - version get quote
 * @property {string[]} [itemIds] - item ids
 * @param {Array<string> | null} ids - array of id or null
 * @returns {QuoteParams} QuoteParams - params of quote
 */
export const getQuoteParams = (ids: Array<string> | null = null) => {
    const params: QuoteParamsType = {};

    /**
     * First case: when we need call quote request without ids, but use version v2.0.
     * Second case: when we need call quote request with ids that enable some item.
     * Third case: When we need enable all items.
     */
    if (ids) params.itemIds = ids.length ? ids : [''];

    params.version = 'v2.0';

    // When is not wizardMs checkout, we not pass version, and using is v1.0
    return params;
};

/**
 * Get data for cart
 * @param {Array<string> | null} ids - array with ids
 * @param {boolean} shouldSkipEEC - a flag indicating whether the EEC_cart_product event should be sent or not
 * @return {ActionType<AxiosPromise>} Action
 */
export const getQuote = createAsyncThunk<
    {data: GetQuoteType},
    {ids?: string[]; shouldSkipEEC?: boolean} | undefined,
    {
        fulfilledMeta: {shouldSkipEEC: boolean};
        rejectValue: {response: {data: {messages: {type: string; message: string}[]}}};
    }
>('CART@GET_QUOTE', async ({ids, shouldSkipEEC} = {}, {fulfillWithValue}) => {
    const {dispatch: dispatchToHost} = apiConfig.hostStore;

    dispatchToHost({
        type: 'CART@GET_QUOTE_PENDING',
    });

    try {
        const response = await apiConfig.instanceGusaBackend({
            headers: {
                [HTTP_REQUEST_HEADER.CONTENT_TYPE]:
                    HTTP_REQUEST_HEADER_CONTENT_TYPE_VALUE.APPLICATION_JSON,
            },
            method: 'get',
            params: getQuoteParams(ids),
            url: ENDPOINTS.GET_QUOTE,
        });
        dispatchToHost({
            payload: response,
            type: 'CART@GET_QUOTE_FULFILLED',
        });
        return fulfillWithValue(response, {shouldSkipEEC: shouldSkipEEC || false});
    } catch (e) {
        dispatchToHost({type: 'CART@GET_QUOTE_REJECTED'});
        throw e;
    }
});

export const getSavedQuote = createAsyncThunk<{data: GetQuoteType}, string>(
    'CART@GET_QUOTE',
    async quoteId => {
        const {dispatch: dispatchToHost} = apiConfig.hostStore;

        dispatchToHost({
            type: 'CART@GET_QUOTE_PENDING',
        });

        try {
            const response = await apiConfig.instanceGusaBackend({
                headers: {
                    [HTTP_REQUEST_HEADER.CONTENT_TYPE]:
                        HTTP_REQUEST_HEADER_CONTENT_TYPE_VALUE.APPLICATION_JSON,
                },
                method: 'get',
                params: {
                    token: quoteId,
                    version: 'v2.0',
                },
                url: ENDPOINTS.GET_QUOTE,
            });
            dispatchToHost({
                payload: response,
                type: 'CART@GET_QUOTE_FULFILLED',
            });
            return response;
        } catch (e) {
            dispatchToHost({type: 'CART@GET_QUOTE_REJECTED'});
            throw e;
        }
    },
);

export const removeQuoteItem = createAsyncThunk<
    {data: GetQuoteType},
    {item: CartItemType; isNotice?: boolean}
>('CART@REMOVE_QUOTE_ITEM', async ({isNotice, item}) => {
    const {dispatch: dispatchToHost} = apiConfig.hostStore;

    dispatchToHost({
        payload: item,
        type: 'CART@REMOVE_QUOTE_ITEM_PENDING',
    });

    try {
        const response = await apiConfig.instanceGusaBackend({
            method: 'get',
            url: `${ENDPOINTS.REMOVE_QUOTE_ITEM}/${item.id}`,
        });
        dispatchToHost({
            meta: isNotice ? {...item, isNotice} : item,
            payload: response,
            type: 'CART@REMOVE_QUOTE_ITEM_FULFILLED',
        });
        return response;
    } catch (e) {
        dispatchToHost({type: 'CART@REMOVE_QUOTE_ITEM_REJECTED'});
        throw e;
    }
});

const initialState: Array<CartItemType> = [];

/**
 * Get cart quote
 *
 * @param {Array<CartItemType>} state - Current state
 * @param {ActionType<AxiosPromise>} action - Received action
 * @returns {Array<CartItemType>} updated state
 */
export const quoteItems = createReducer<Array<CartItemType>>(initialState, builder =>
    builder
        .addCase(setDataFromHostStore, (state, action) => action.payload?.quoteItems || state)
        .addCase(getQuote.fulfilled, (_, action) => action.payload?.data.quoteItems)
        .addCase(removeQuoteItem.fulfilled, (_, action) => action.payload?.data.quoteItems)
        .addCase(getQuote.rejected, () => [])
        .addCase(removeQuoteItem.rejected, () => [])
        .addCase(removeQuoteItem.pending, (state, action) =>
            state.filter(({id}: CartItemType): boolean => id !== action.meta.arg?.item?.id),
        ),
);
